import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import CircularProgress from '@mui/joy/CircularProgress';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Stack from '@mui/joy/Stack';
import Accordion from '@mui/joy/Accordion';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionDetails from '@mui/joy/AccordionDetails';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Link from "@mui/joy/Link";

import { NotificationService } from "../../common/notification";
import { Game } from "../../models/games";
import { AdminGameFilterInfo } from "../../models/gameFilters";
import { gamesListApi } from "./gamesApi";
import { gameFiltersListApi, updateGameFiltersApi, updateGameFiltersGroupApi } from "./gameFiltersApi";
import { GameFilterEditorDialog } from "./GameFilterEditorDialog";
import { GameFilterGroupEditorDialog } from "./GameFilterGroupEditorDialog";
import {
    FiltersGroup,
    groupFilters,
    normalizeColor,
    renderAvailability,
    renderColorShape,
    renderFilterRegion,
    withParens
} from "./gameFiltersUtils";

export function GameFiltersTab() {
    const [loadingGames, setLoadingGames] = React.useState(false);
    const [loadingFilters, setLoadingFilters] = React.useState(false);
    const [games, setGames] = React.useState<Game[]>([]);
    const [selectedGameId, setSelectedGameId] = React.useState<number | null>(null);
    const [filters, setFilters] = React.useState<AdminGameFilterInfo[]>([]);
    const [expandedGroup, setExpandedGroup] = React.useState<string | null>(null);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [selectedFilter, setSelectedFilter] = React.useState<AdminGameFilterInfo | null>(null);
    const [groupEditorOpen, setGroupEditorOpen] = React.useState(false);
    const [groupFiltersState, setGroupFiltersState] = React.useState<AdminGameFilterInfo[]>([]);

    React.useEffect(() => {
        loadGames().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!selectedGameId) {
            setFilters([]);
            return;
        }
        loadFilters(selectedGameId).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGameId]);

    const loadGames = async () => {
        setLoadingGames(true);
        try {
            const res = await gamesListApi();
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
                return;
            }
            const list = (res.value ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
            setGames(list);
            if (list.length > 0) {
                // По умолчанию выбираем TF2 (id = 440), если есть, иначе первую игру
                const preferred = list.find(g => g.id === 440) ?? list[0];
                setSelectedGameId(prev => prev ?? preferred.id);
            }
        } finally {
            setLoadingGames(false);
        }
    };

    const loadFilters = async (gameId: number) => {
        setLoadingFilters(true);
        try {
            const res = await gameFiltersListApi(gameId);
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
                setFilters([]);
                return;
            }
            const list = res.value ?? [];
            setFilters(list);
            // По умолчанию все группы свернуты
            setExpandedGroup(null);
        } finally {
            setLoadingFilters(false);
        }
    };

    const groups: FiltersGroup[] = React.useMemo(() => groupFilters(filters), [filters]);
    const pageLoading = loadingGames || loadingFilters || saving;

    const openEditor = (f: AdminGameFilterInfo) => {
        setSelectedFilter(f);
        setEditorOpen(true);
    };

    const saveFilter = async (updated: AdminGameFilterInfo) => {
        if (!selectedGameId) return;
        setSaving(true);
        try {
            const res = await updateGameFiltersApi(selectedGameId, updated);
            if (!res.result) {
                NotificationService.alertError("Не удалось сохранить фильтр. Попробуйте позже.");
                return;
            }
            NotificationService.alertSuccess("Фильтр сохранён");
            await loadFilters(selectedGameId);
        } finally {
            setSaving(false);
        }
    };

    const openGroupEditor = (group: FiltersGroup) => {
        setGroupFiltersState(group.items);
        setGroupEditorOpen(true);
    };

    const saveGroup = async (updated: AdminGameFilterInfo[]) => {
        if (!selectedGameId) return;
        if (!updated.length) return;
        setSaving(true);
        try {
            const res = await updateGameFiltersGroupApi(selectedGameId, updated);
            if (!res.result) {
                NotificationService.alertError("Не удалось сохранить группу фильтров. Попробуйте позже.");
                return;
            }
            NotificationService.alertSuccess("Группа фильтров сохранена");
            setGroupEditorOpen(false);
            await loadFilters(selectedGameId);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {editorOpen && selectedFilter && (
                <GameFilterEditorDialog
                    filter={selectedFilter}
                    open={editorOpen}
                    setOpen={setEditorOpen}
                    onConfirm={saveFilter}
                />
            )}
            {groupEditorOpen && groupFiltersState.length > 0 && (
                <GameFilterGroupEditorDialog
                    open={groupEditorOpen}
                    setOpen={setGroupEditorOpen}
                    filters={groupFiltersState}
                    onConfirm={saveGroup}
                    isSaving={saving}
                />
            )}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'flex-end' } }}>
                    <FormControl size="sm" sx={{ minWidth: 260, flex: 1 }}>
                        <FormLabel>Игра</FormLabel>
                        <Select
                            value={selectedGameId}
                            onChange={(_, v) => setSelectedGameId((v as number) ?? null)}
                            placeholder={loadingGames ? 'Загрузка...' : 'Выберите игру'}
                        >
                            {games.map(g => (
                                <Option key={g.id} value={g.id}>
                                    {g.name} ({g.id})
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    {pageLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size="sm" />
                            <Typography level="body-sm">Загрузка</Typography>
                        </Box>
                    )}
                </Stack>
            </Sheet>

            {!selectedGameId ? (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                    <Typography level="body-sm">Выберите игру, чтобы загрузить фильтры.</Typography>
                </Sheet>
            ) : loadingFilters ? (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: '8px', display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Sheet>
            ) : groups.length === 0 ? (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                    <Typography level="body-sm">Фильтры не найдены.</Typography>
                </Sheet>
            ) : (
                groups.map(group => (
                    <Accordion
                        key={group.key}
                        expanded={expandedGroup === group.key}
                        onChange={(_, expanded) => setExpandedGroup(expanded ? group.key : null)}
                    >
                        <AccordionSummary>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                                <Typography level="title-md" sx={{ mr: 1 }}>
                                    {withParens(group.groupNameRu, group.groupNameEn)}
                                </Typography>
                                <Chip size="sm" variant="outlined">
                                    {renderFilterRegion(group.filterRegion)}
                                </Chip>
                                <Chip size="sm" variant="soft">
                                    Где доступно: {renderAvailability(group.items[0]?.availability)}
                                </Chip>
                                <Typography level="body-xs" sx={{ ml: 'auto', opacity: 0.75 }}>
                                    {group.items.length} шт.
                                </Typography>
                                <Link
                                    level="body-sm"
                                    component="span"
                                    disabled={saving}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openGroupEditor(group);
                                    }}
                                    sx={{ ml: 1, whiteSpace: 'nowrap', cursor: 'pointer' }}
                                >
                                    Редактировать группу
                                </Link>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List sx={{ '--ListItem-paddingY': '6px' }}>
                                {group.items.map(item => (
                                    <ListItem key={item.id}>
                                        <ListItemContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                {item.icon ? (
                                                    <Avatar size="sm" variant="outlined" src={item.icon ?? undefined} />
                                                ) : (
                                                    <Avatar size="sm" variant="outlined">
                                                        {String(item.id)}
                                                    </Avatar>
                                                )}
                                                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {(() => {
                                                        const shape = renderColorShape(item.color, item.colorType);
                                                        if (!shape) return null;
                                                        if (shape.variant === "star") {
                                                            return (
                                                                <Typography
                                                                    level="body-sm"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        ...(shape.second
                                                                            ? {
                                                                                backgroundImage: shape.fill,
                                                                                WebkitBackgroundClip: 'text',
                                                                                backgroundClip: 'text',
                                                                                color: 'transparent',
                                                                            }
                                                                            : { color: shape.first }),
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    ★
                                                                </Typography>
                                                            );
                                                        }
                                                        return (
                                                            <Box
                                                                sx={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: shape.borderRadius,
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    ...(shape.second
                                                                        ? { backgroundImage: shape.fill }
                                                                        : { backgroundColor: shape.fill }),
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        );
                                                    })()}
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            level="body-sm"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: normalizeColor(item.textColor),
                                                            }}
                                                        >
                                                            {withParens(item.titleRu, item.titleEn)}
                                                        </Typography>
                                                        {item.filterType === 4 && item.defaultValue && (
                                                            <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                                                                Выбран по умолчанию
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                    <Link
                                                        level="body-sm"
                                                        component="button"
                                                        disabled={saving}
                                                        onClick={() => openEditor(item)}
                                                        sx={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        Редактировать
                                                    </Link>
                                                </Stack>
                                            </Box>
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}
        </Box>
    );
}

